"use client"

import { FC, useState, useCallback, useMemo } from "react"
import { ChevronLeft, ChevronRight } from "lucide-react"
import { StaticImageData } from "next/image"
import { pricingPlans as staticPlans } from '@/src/utils/priceHelper'
import { useContent } from "@/src/context/ContentContext"
import { useLanguage } from "@/src/context/LanguageContext"
import { useUI } from "@/src/lib/i18n/ui"

interface PricingDetailProps {
  id: string
  price: string
  title: string
  features: string[]
  images: (string | StaticImageData)[]
}

const PricingDetail: FC<PricingDetailProps> = ({ id, price, title, features, images }) => {
  const [currentImageIndex, setCurrentImageIndex] = useState(0)
  const { lang } = useLanguage()
  const t = useUI(lang)

  const nextImage = useCallback(() => {
    setCurrentImageIndex((prev) => (prev + 1) % images.length)
  }, [images.length])

  const prevImage = useCallback(() => {
    setCurrentImageIndex((prev) => (prev - 1 + images.length) % images.length)
  }, [images.length])

  const scrollToContact = useCallback(() => {
    const element = document.getElementById("contact-title")
    if (element) {
      const y = element.getBoundingClientRect().top + window.pageYOffset - 100
      window.scrollTo({ top: y, behavior: "smooth" })
    }
  }, [])

  return (
    <section
      id={id}
      className="py-8 min-h-full lg:min-h-screen flex items-center bg-gray-900"
      style={{
        backgroundImage: "url('/icons/background_light.png')",
        backgroundSize: "cover",
        backgroundPosition: "center",
      }}
    >
      <div className="container mx-auto">
        <div className="grid lg:grid-cols-2 gap-12 items-center">
          <div>
            <div className="mb-8">
              <h3 className="text-4xl lg:text-4xl font-semibold text-white mb-4">
                {price} <span className="text-2xl lg:text-3xl text-gray-400">{t("squareMeter")}</span>
              </h3>
              <h4 className="text-xl lg:text-2xl font-light text-gray-300 mb-6">{title}</h4>
            </div>
            <ul className="space-y-3 mb-8 font-light">
              {features.map((feature, index) => (
                <li key={index} className="flex items-start text-gray-300">
                  <span className="text-white mr-3 mt-1">•</span>
                  <span className="text-base lg:text-lg">{feature}</span>
                </li>
              ))}
            </ul>
            <button
              onClick={scrollToContact}
              className="w-full lg:w-fit px-8 py-3 bg-transparent border border-white text-white rounded-full hover:bg-white hover:text-black transition-all duration-300 text-lg font-light"
            >
              {t("pricingCta")}
            </button>
          </div>
          <div className="relative">
            <div className="aspect-[4/3] bg-gray-800 rounded-lg overflow-hidden">
              <img
                src={
                  typeof images[currentImageIndex] === "string"
                    ? (images[currentImageIndex] as string)
                    : (images[currentImageIndex] as StaticImageData).src
                }
                alt={`${title} - ${t("pricingPhotoAlt")} ${currentImageIndex + 1}`}
                className="w-full h-full object-cover"
              />
            </div>
            <button
              onClick={prevImage}
              className="absolute left-4 top-1/2 -translate-y-1/2 bg-black/50 hover:bg-black/70 text-white p-2 rounded-full transition-colors"
              aria-label="Previous photo"
            >
              <ChevronLeft size={24} />
            </button>
            <button
              onClick={nextImage}
              className="absolute right-4 top-1/2 -translate-y-1/2 bg-black/50 hover:bg-black/70 text-white p-2 rounded-full transition-colors"
              aria-label="Next photo"
            >
              <ChevronRight size={24} />
            </button>
          </div>
        </div>
      </div>
    </section>
  )
}

interface PricingDetailsProps {
  activeId: string
}

const PricingDetails: FC<PricingDetailsProps> = ({ activeId }) => {
  const { pricing } = useContent()

  const activePlan = useMemo(() => {
    const contentPlan = pricing.find((p) => p.id === activeId)
    const staticPlan = staticPlans.find((p) => p.id === activeId)
    if (!staticPlan) return null
    return {
      ...staticPlan,
      price: contentPlan?.price ?? staticPlan.price,
      title: contentPlan?.title ?? staticPlan.title,
      features: contentPlan?.features ?? staticPlan.features,
    }
  }, [activeId, pricing])

  if (!activePlan) return null

  return <PricingDetail key={activePlan.id} {...activePlan} />
}

export default PricingDetails
